/**
 * LeaveDateRangeField
 *
 * The start and end of an absence, as Congés has always asked for them: a date plus a half
 * day — the start in the morning ("Matin") or at noon ("Midi"), the end at noon or in the
 * evening ("Soir"). Two FormRows, so it sits in a FormDialog like any other field.
 *
 * The value is `{ start_date, start_period, end_date, end_period }` with `YYYY-MM-DD` dates
 * and MORNING / NOON / EVENING periods. The helpers below convert it to and from the
 * planning's timestamps with the encoding Congés uses: a start at 00:00 or 12:00, an end at
 * 12:00 or at the end of the day.
 */
import React from "react";
import { MenuItem, Stack, TextField } from "@mui/material";
import moment from "moment-timezone";
import Langs, { Localize } from "../../app/lang/langs";
import { FormRow, formFieldSx } from "../popups/FormDialog";
import FormDatePicker from "./FormDatePicker";

export const START_PERIODS = ["MORNING", "NOON"];
export const END_PERIODS = ["NOON", "EVENING"];

const PERIOD_LABEL_KEYS = { MORNING: "Matin", NOON: "Midi", EVENING: "Soir" };

/** The translation key naming a period. */
export const periodLabelKey = (period) => PERIOD_LABEL_KEYS[period] || "";

const inZone = (value, timezone) => (timezone ? moment(Number(value)).tz(timezone) : moment(Number(value)));

/** The field's value from a planning leave's `from` / `to` (epoch ms). */
export function leavePeriodFromTimestamps(from, to, timezone) {
  if (!from || !to) return { start_date: "", start_period: "MORNING", end_date: "", end_period: "EVENING" };
  const start = inZone(from, timezone);
  const end = inZone(to, timezone);
  return {
    start_date: start.format("YYYY-MM-DD"),
    start_period: start.hours() >= 12 ? "NOON" : "MORNING",
    end_date: end.format("YYYY-MM-DD"),
    end_period: end.hours() > 12 ? "EVENING" : "NOON",
  };
}

/** The planning leave's `from` / `to` (epoch ms) from the field's value. */
export function leaveTimestampsFromPeriod(value, timezone) {
  const day = (date) => (timezone ? moment.tz(date, "YYYY-MM-DD", timezone) : moment(date, "YYYY-MM-DD"));
  const from = day(value.start_date).startOf("day");
  if (value.start_period === "NOON") from.hours(12);
  const to = value.end_period === "NOON"
    ? day(value.end_date).startOf("day").hours(12)
    : day(value.end_date).endOf("day");
  return { from: from.valueOf(), to: to.valueOf() };
}

/** Whether the period is non-empty — "Monday noon → Monday noon" is not an absence. */
export function isLeavePeriodValid(value) {
  if (!value.start_date || !value.end_date) return true;
  const { from, to } = leaveTimestampsFromPeriod(value);
  return to > from;
}

/**
 * @param {Object} props.value - { start_date, start_period, end_date, end_period }
 * @param {Function} props.onChange - called with the whole new value
 * @param {Object} [props.errors] - { start_date, end_date } messages
 * @param {boolean} [props.showPeriods] - false for plain calendar dates (the suivi's own)
 * @param {boolean} [props.required]
 * @param {boolean} [props.disabled]
 */
export default function LeaveDateRangeField({ value, onChange, errors = {}, showPeriods = true, required, disabled }) {
  const set = (field, fieldValue) => {
    const next = { ...value, [field]: fieldValue };
    // Picking the start fills the end with the same day, as Congés always did: most absences
    // are a single day. The end follows the start while it has not been set on its own (empty,
    // or still equal to the previous start), and is dragged along rather than left inverted.
    if (field === "start_date" && fieldValue) {
      const endFollowsStart = !value.end_date || value.end_date === value.start_date;
      if (endFollowsStart || next.end_date < fieldValue) next.end_date = fieldValue;
    }
    onChange(next);
  };

  const row = (labelKey, dateField, periodField, periods) => (
    <FormRow label={<Langs str={labelKey} />} required={required}>
      <Stack direction="row" spacing={1}>
        <FormDatePicker
          value={value[dateField] || ""}
          onChange={(date) => set(dateField, date)}
          // The end cannot be picked before the start.
          minDate={dateField === "end_date" ? value.start_date : undefined}
          error={!!errors[dateField]}
          helperText={errors[dateField]}
          disabled={disabled}
        />
        {showPeriods && (
          <TextField
            select
            size="small"
            value={value[periodField]}
            onChange={(e) => set(periodField, e.target.value)}
            disabled={disabled}
            sx={{ ...formFieldSx, minWidth: 140 }}
          >
            {periods.map((period) => (
              <MenuItem key={period} value={period}>{Localize(periodLabelKey(period))}</MenuItem>
            ))}
          </TextField>
        )}
      </Stack>
    </FormRow>
  );

  return (
    <>
      {row("START", "start_date", "start_period", START_PERIODS)}
      {row("END", "end_date", "end_period", END_PERIODS)}
    </>
  );
}
